import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Context } from "../../../contexts/Store";
import { baseConfig } from "../../../axiosConfig";
import styled from "styled-components";
import ProgramVideoSection from "../../includes/programsCommonComponents/singlePageBasedOnType/ProgramVideoSection";
import Loader from "../../includes/loader/Loader";
import EmptyPage from "../../includes/emptyPages/EmptyPage";
import emptyPic from "../../../assets/icons/webinar-empty-icon.svg";
import LockIcon from "../../../assets/icons/lock-icon.svg";

function FreeCourseView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { state } = useContext(Context);
    const accessToken = state.user_details.access_token;
    const [isLoading, setLoading] = useState(true);
    const [courseData, setCourseData] = useState({});
    const [contents, setContents] = useState([]);
    const [selectedContent, setSelectedContent] = useState(null);
    const [topics, setTopics] = useState([]);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [reload, setReload] = useState(false);

    // Get content ID from URL if present
    const searchParams = new URLSearchParams(location.search);
    const contentId = searchParams.get("content");

    const handleEnroll = async () => {
        var formdata = new FormData();
        formdata.append("course", id);
        try {
            baseConfig
                .post(`/students/enroll-free-course/`, formdata, {
                    headers: {
                        Authorization: "Bearer " + accessToken,
                    },
                })
                .then((response) => {
                    const { StatusCode, data } = response.data;
                    if (StatusCode === 6000) {
                        window.location.reload();
                    } else {
                        alert("Some error occurred");
                    }
                })
                .catch((error) => {
                    if (error?.response?.data?.code === "token_not_valid") {
                        localStorage.clear();
                        window.location = "/";
                    }
                });
        } catch (error) {
            alert("Some error occurred");
        }
    };

    useEffect(() => {
        const fetchCourseData = async () => {
            setLoading(true);
            try {
                const response = await baseConfig.get(
                    `/students/enrolled-free-course-details/${id}`,
                    {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }
                );

                const { StatusCode, data } = response.data;
                if (StatusCode === 6000) {
                    setCourseData(data);

                    if (data.contents && data.contents.length > 0) {
                        setContents(data.contents);
                        // If content ID is in URL, select that content
                        if (contentId) {
                            const content = data.contents.find(
                                (c) => c.id === parseInt(contentId)
                            );
                            if (content && !content.is_locked) {
                                setSelectedContent(content);
                            }
                        }
                    }

                    if (data.topics && data.topics.length > 0) {
                        setTopics(data.topics);
                        // If content ID is in URL, find and select the corresponding topic
                        if (contentId) {
                            const topic = data.topics.find((t) =>
                                t.contents.some(
                                    (c) => c.id === parseInt(contentId)
                                )
                            );
                            if (topic) {
                                setSelectedTopic(topic);
                            }
                        } else {
                            setSelectedTopic(data.topics[0]);
                        }
                    }
                } else {
                    // alert("Error loading course data");
                }
            } catch (error) {
                console.error("Error fetching course data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchCourseData();
    }, [id, reload, contentId]);

    const updateContentStatus = async (contentId) => {
        try {
            const response = await baseConfig.post(
                `/students/mark-as-complete/${contentId}/`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const { StatusCode, data } = response.data;
            if (StatusCode === 6000) {
                if (topics && topics.length > 0) {
                    const updatedTopics = topics.map((topic) => {
                        if (topic.contents) {
                            const updatedContents = topic.contents.map(
                                (content) => {
                                    if (content.id === contentId) {
                                        return {
                                            ...content,
                                            status: "completed",
                                            is_locked: false,
                                        };
                                    }
                                    if (
                                        data.next_content &&
                                        content.id === data.next_content
                                    ) {
                                        return { ...content, is_locked: false };
                                    }
                                    return content;
                                }
                            );
                            return { ...topic, contents: updatedContents };
                        }
                        return topic;
                    });
                    setTopics(updatedTopics);
                } else {
                    const updatedContents = contents.map((content) => {
                        if (content.id === contentId) {
                            return {
                                ...content,
                                status: "completed",
                                is_locked: false,
                            };
                        }

                        if (
                            data.next_content &&
                            content.id === data.next_content
                        ) {
                            return { ...content, is_locked: false };
                        }
                        return content;
                    });
                    setContents(updatedContents);
                }

                if (data.next_content) {
                    const nextContent =
                        topics && topics.length > 0
                            ? topics
                                .flatMap((topic) => topic.contents || [])
                                .find(
                                    (content) =>
                                        content.id === data.next_content
                                )
                            : contents.find(
                                (content) => content.id === data.next_content
                            );

                    if (nextContent) {
                        setSelectedContent(nextContent);
                    }
                } else {
                    // Show completion message and reload page
                    alert(
                        "Congratulations! You have completed all the content in this course."
                    );
                    window.location.reload();
                }

                setReload((prev) => !prev);
            } else {
            }
        } catch (error) {
            console.error("Error updating content status:", error);
        }
    };

    const handleContentSelect = (content) => {
        if (content.is_locked) return;
        setSelectedContent(content);
        // Update URL with content ID
        navigate(`/free-courses/${id}?content=${content.id}`);
    };

    const handleTopicSelect = (topic) => {
        if (selectedTopic && selectedTopic.id === topic.id) {
            // If clicking the same topic, collapse it
            setSelectedTopic(null);
            setSelectedContent(null);
        } else {
            // Set the new selected topic
            setSelectedTopic(topic);

            // Find the first unlocked content in the topic
            if (topic.contents && topic.contents.length > 0) {
                const firstUnlockedContent = topic.contents.find(
                    (content) => !content.is_locked
                );

                if (firstUnlockedContent) {
                    setSelectedContent(firstUnlockedContent);
                } else {
                    setSelectedContent(null);
                }
            } else {
                setSelectedContent(null);
            }
        }
    };

    return (
        <MainContainer>
            <TopContainer>
                <TextDiv>
                    {selectedContent?.title ? (
                        <>
                            <PathText
                                onClick={() => {
                                    navigate("/free-courses/");
                                }}
                            >
                                Free Course
                            </PathText>
                            <Slash>&nbsp;/&nbsp;</Slash>
                            <PathText
                                onClick={() => {
                                    setSelectedContent(null);
                                }}
                            >
                                {courseData.title}
                            </PathText>
                            <Slash>&nbsp;/&nbsp;</Slash>
                            <PathTextBlack>
                                {selectedContent?.title}
                            </PathTextBlack>
                        </>
                    ) : (
                        <>
                            {" "}
                            <PathText
                                onClick={() => {
                                    navigate("/free-courses/");
                                }}
                            >
                                Free Course
                            </PathText>
                            <Slash>&nbsp;/&nbsp;</Slash>
                            <PathTextBlack>{courseData.title}</PathTextBlack>
                        </>
                    )}
                </TextDiv>
            </TopContainer>

            {isLoading ? (
                <Loader />
            ) : (
                <>
                    {(contents && contents.length > 0) ||
                        (topics && topics.length > 0) ? (
                        <ContentBox>
                            <LeftPart>
                                {selectedContent ? (
                                    <ProgramVideoSection
                                        data={selectedContent}
                                        updateContentStatus={
                                            updateContentStatus
                                        }
                                    />
                                ) : (
                                    <>
                                        <CourseImage
                                            src={courseData.image}
                                            alt={courseData.title}
                                        />
                                        <CourseInfoContainer>
                                            <CourseTitle>
                                                {courseData.title}
                                                {courseData?.enrolled ===
                                                    false && (
                                                        <EnrollButton
                                                            onClick={() => {
                                                                handleEnroll();
                                                            }}
                                                        >
                                                            Enroll Now
                                                        </EnrollButton>
                                                    )}
                                            </CourseTitle>
                                            {courseData.instructor_data && (
                                                <InstructorInfo>
                                                    <InstructorImage
                                                        src={
                                                            courseData
                                                                .instructor_data
                                                                .image
                                                        }
                                                        alt={
                                                            courseData
                                                                .instructor_data
                                                                .name
                                                        }
                                                    />
                                                    <InstructorDetails>
                                                        <InstructorDesignation>
                                                            {
                                                                courseData
                                                                    .instructor_data
                                                                    .designation
                                                            }
                                                        </InstructorDesignation>
                                                        <InstructorName>
                                                            {
                                                                courseData
                                                                    .instructor_data
                                                                    .name
                                                            }
                                                        </InstructorName>
                                                    </InstructorDetails>
                                                </InstructorInfo>
                                            )}
                                        </CourseInfoContainer>
                                        <CourseSectionTitle>
                                            About Lessons
                                        </CourseSectionTitle>
                                        <CourseDescription>
                                            {courseData.description}
                                        </CourseDescription>
                                        <CourseSectionTitle>
                                            Skills you will learn
                                        </CourseSectionTitle>

                                        <SkillsContainer>
                                            {courseData.skills_learn_data &&
                                                courseData.skills_learn_data.map(
                                                    (skill, index) => (
                                                        <SkillTag key={index}>
                                                            {skill.name}
                                                        </SkillTag>
                                                    )
                                                )}
                                        </SkillsContainer>
                                    </>
                                )}
                            </LeftPart>

                            <RightPart>
                                {topics && topics.length > 0 ? (
                                    <>
                                        <TextHead>Topic</TextHead>
                                        {topics.map((topic, index) => (
                                            <TopicContainer key={index}>
                                                <TopicHeader
                                                    onClick={() =>
                                                        handleTopicSelect(topic)
                                                    }
                                                    isActive={
                                                        selectedTopic &&
                                                        selectedTopic.id ===
                                                        topic.id
                                                    }
                                                >
                                                    <TopicTitle>
                                                        {topic.title}
                                                    </TopicTitle>
                                                    <TopicArrow
                                                        isOpen={
                                                            selectedTopic &&
                                                            selectedTopic.id ===
                                                            topic.id
                                                        }
                                                    >
                                                        ▼
                                                    </TopicArrow>
                                                </TopicHeader>

                                                {selectedTopic &&
                                                    selectedTopic.id ===
                                                    topic.id &&
                                                    topic.contents && (
                                                        <TopicContentsList
                                                            isOpen={
                                                                selectedTopic &&
                                                                selectedTopic.id ===
                                                                topic.id
                                                            }
                                                        >
                                                            {topic.contents.map(
                                                                (
                                                                    content,
                                                                    cIndex
                                                                ) => (
                                                                    <ContentItem
                                                                        key={
                                                                            cIndex
                                                                        }
                                                                        isActive={
                                                                            selectedContent &&
                                                                            selectedContent.id ===
                                                                            content.id
                                                                        }
                                                                        onClick={() => {
                                                                            content.is_locked
                                                                                ? null
                                                                                : handleContentSelect(
                                                                                    content
                                                                                );
                                                                        }}
                                                                    >
                                                                        <ContentThumbnail>
                                                                            <img
                                                                                src={
                                                                                    content.thumbnail
                                                                                }
                                                                                alt={
                                                                                    content.title
                                                                                }
                                                                            />
                                                                            {content.is_locked ? (
                                                                                <PlayIcon>
                                                                                    <img
                                                                                        src={
                                                                                            LockIcon
                                                                                        }
                                                                                        alt="icon"
                                                                                    />
                                                                                </PlayIcon>
                                                                            ) : (
                                                                                <PlayIcon>
                                                                                    ▶
                                                                                </PlayIcon>
                                                                            )}
                                                                        </ContentThumbnail>
                                                                        <ContentInfo>
                                                                            <ContentTitle>
                                                                                Lesson{" "}
                                                                                {cIndex +
                                                                                    1}{" "}
                                                                                {content.duration &&
                                                                                    `| ${content.duration}`}
                                                                            </ContentTitle>
                                                                            <ContentName>
                                                                                {
                                                                                    content.title
                                                                                }
                                                                            </ContentName>
                                                                        </ContentInfo>
                                                                    </ContentItem>
                                                                )
                                                            )}
                                                        </TopicContentsList>
                                                    )}
                                            </TopicContainer>
                                        ))}
                                    </>
                                ) : (
                                    <>
                                        {contents.map((content, index) => (
                                            <ContentItem
                                                key={index}
                                                isActive={
                                                    selectedContent &&
                                                    selectedContent.id ===
                                                    content.id
                                                }
                                                onClick={() => {
                                                    content.is_locked
                                                        ? null
                                                        : handleContentSelect(
                                                            content
                                                        );
                                                }}
                                            >
                                                <ContentThumbnail>
                                                    <img
                                                        src={content.thumbnail}
                                                        alt={content.title}
                                                    />
                                                    {content.is_locked ? (
                                                        <PlayIcon>
                                                            <img
                                                                src={LockIcon}
                                                                alt="icon"
                                                            />
                                                        </PlayIcon>
                                                    ) : (
                                                        <PlayIcon>▶</PlayIcon>
                                                    )}
                                                </ContentThumbnail>
                                                <ContentInfo>
                                                    <ContentTitle>
                                                        Lesson {index + 1}{" "}
                                                        {content.duration &&
                                                            `| ${content.duration}`}
                                                    </ContentTitle>
                                                    <ContentName>
                                                        {content.title}
                                                    </ContentName>
                                                </ContentInfo>
                                            </ContentItem>
                                        ))}
                                    </>
                                )}
                            </RightPart>
                        </ContentBox>
                    ) : (
                        <>
                            <ContentBox>
                                <LeftPart>
                                    {selectedContent ? (
                                        <ProgramVideoSection
                                            data={selectedContent}
                                            updateContentStatus={
                                                updateContentStatus
                                            }
                                        />
                                    ) : (
                                        <>
                                            <CourseImage
                                                src={courseData.image}
                                                alt={courseData.title}
                                            />
                                            <CourseInfoContainer>
                                                <CourseTitle>
                                                    {courseData.title}
                                                    {courseData?.enrolled ===
                                                        false && (
                                                            <EnrollButton
                                                                onClick={() => {
                                                                    handleEnroll();
                                                                }}
                                                            >
                                                                Enroll Now
                                                            </EnrollButton>
                                                        )}
                                                </CourseTitle>
                                                {courseData.instructor_data && (
                                                    <InstructorInfo>
                                                        <InstructorImage
                                                            src={
                                                                courseData
                                                                    .instructor_data
                                                                    .image
                                                            }
                                                            alt={
                                                                courseData
                                                                    .instructor_data
                                                                    .name
                                                            }
                                                        />
                                                        <InstructorDetails>
                                                            <InstructorDesignation>
                                                                {
                                                                    courseData
                                                                        .instructor_data
                                                                        .designation
                                                                }
                                                            </InstructorDesignation>
                                                            <InstructorName>
                                                                {
                                                                    courseData
                                                                        .instructor_data
                                                                        .name
                                                                }
                                                            </InstructorName>
                                                        </InstructorDetails>
                                                    </InstructorInfo>
                                                )}
                                            </CourseInfoContainer>
                                            <CourseSectionTitle>
                                                About Lessons
                                            </CourseSectionTitle>
                                            <CourseDescription>
                                                {courseData.description}
                                            </CourseDescription>
                                            <CourseSectionTitle>
                                                Skills you will learn
                                            </CourseSectionTitle>

                                            <SkillsContainer>
                                                {courseData.skills_learn_data &&
                                                    courseData.skills_learn_data.map(
                                                        (skill, index) => (
                                                            <SkillTag
                                                                key={index}
                                                            >
                                                                {skill.name}
                                                            </SkillTag>
                                                        )
                                                    )}
                                            </SkillsContainer>
                                        </>
                                    )}
                                </LeftPart>
                                <RightPart>
                                    <EmptyPage
                                        img={emptyPic}
                                        title={"No content available"}
                                    />
                                </RightPart>
                            </ContentBox>
                        </>
                    )}
                </>
            )}
        </MainContainer>
    );
}

export default FreeCourseView;

const MainContainer = styled.div`
    background: #ffffff;
    padding: 20px;
    border: 1px solid #e5e5e5;
    box-sizing: border-box;
    border-radius: 10px;
    height: auto;
    min-height: calc(100vh - 110px);
`;

const TopContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
`;

const TextDiv = styled.div``;

const PathText = styled.span`
    display: inline-block;
    font-size: 14px;
    color: #9d9d9d;
    cursor: pointer;
    &:hover {
        text-decoration: underline;
    }
`;

const Slash = styled.span`
    display: inline-block;
    font-size: 14px;
    color: #9d9d9d;
`;

const PathTextBlack = styled.span`
    color: #000;
    display: inline-block;
    font-size: 14px;
`;

const CourseImage = styled.img`
    width: 100%;
    height: 350px;
    object-fit: cover;
    border-radius: 10px;
    margin-bottom: 20px;

    @media (max-width: 950px) {
        width: 100%;
        height: auto;
    }
`;

const TextHead = styled.h1`
    font-size: 20px;
    font-weight: bold;
    margin-bottom: 15px;
`;

const CourseInfoContainer = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`;

const CourseTitle = styled.h1`
    font-size: 28px;
    margin-bottom: 15px;
    font-weight: bold;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const InstructorInfo = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 15px;
`;

const InstructorImage = styled.img`
    width: 48px;
    height: 48px;
    border-radius: 50%;
    margin-right: 12px;
    object-fit: cover;
`;

const InstructorDetails = styled.div``;

const InstructorName = styled.h3`
    font-size: 16px;
    margin: 0;
    font-weight: bold;
`;

const InstructorDesignation = styled.p`
    font-size: 14px;
    color: #666;
    margin: 0;
`;

const SkillsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 20px;
`;

const SkillTag = styled.div`
    background: #f6f4ff;
    padding: 6px 12px;
    border-radius: 8px;
    font-size: 14px;
    color: #404040;
    border: 1px solid #917afd;
`;

const EnrollButton = styled.button`
    background: #522f91;
    color: white;
    border: none;
    padding: 10px;
    border-radius: 8px;
    font-weight: bold;
    cursor: pointer;
    align-self: flex-start;
    transition: background 0.3s;
    &:hover {
        background: #5832c7;
    }
`;

const CourseSectionTitle = styled.h2`
    margin: 30px 0 20px;
    font-size: 22px;
    font-weight: bold;
`;

const ContentBox = styled.div`
    display: flex;
    min-height: 500px;
    gap: 10px;
    justify-content: space-between;
    @media (max-width: 980px) {
        flex-direction: column;
    }
`;

const LeftPart = styled.div`
    flex: 1;
    min-width: 0;
    border-right: 2px dotted #e7e7e7;
    padding-right: 10px;
    @media (max-width: 950px) {
        width: 100%;
        margin-bottom: 20px;
    }
`;

const RightPart = styled.div`
    width: 360px;
    max-height: 800px;
    overflow-y: auto;
    padding: 0 5px;

    @media (max-width: 950px) {
        width: 100%;
        max-height: 400px;
    }
`;

const TopicContainer = styled.div`
    border: 2px solid #e7e7e7;
    border-radius: 10px;
    padding: 4px;
    margin-bottom: 10px;
    overflow: hidden;
    transition: all 0.3s ease;
    width: 100%;
`;

const TopicHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    cursor: pointer;
`;

const TopicTitle = styled.h3`
    font-size: 16px;
    margin: 0;
`;

const TopicArrow = styled.span`
    transform: ${(props) => (props.isOpen ? "rotate(0deg)" : "rotate(-90deg)")};
    transition: transform 0.3s ease;
    font-size: 12px;
    display: inline-block;
`;

const TopicContentsList = styled.div`
    background: #fff;
    border-radius: 8px;
    padding: 8px;
    max-height: ${(props) => (props.isOpen ? "1000px" : "0")};
    opacity: ${(props) => (props.isOpen ? "1" : "0")};
    transition: max-height 0.5s ease, opacity 0.3s ease;
    overflow: hidden;
`;

const ContentItem = styled.div`
    display: flex;
    align-items: center;
    padding: 8px;
    cursor: pointer;
    border: 2px solid #e5e5ea;
    background: ${(props) => (props.isActive ? "#f0f0f0" : "transparent")};
    margin-bottom: 8px;
    border-radius: 10px;
    height: 84px;
    width: 100%;
    box-sizing: border-box;

    &:hover {
        background: #f0f0f0;
    }
`;

const ContentThumbnail = styled.div`
    position: relative;
    width: 120px;
    height: 68px;
    min-width: 120px;
    margin-right: 15px;
    overflow: hidden;
    border-radius: 6px;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 6px;
        transition: transform 0.3s ease;
    }

    &:hover img {
        transform: scale(1.1);
    }
`;

const PlayIcon = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(0, 0, 0, 0.6);
    color: white;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
        width: 20px;
        height: 20px;
    }
`;

const ContentInfo = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    max-height: 68px;
    overflow: hidden;
`;

const ContentTitle = styled.div`
    font-size: 13px;
    color: #888;
    margin-bottom: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ContentName = styled.h4`
    font-size: 15px;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const CourseDescription = styled.div`
    line-height: 1.6;
    color: #555;
    white-space: pre-line;
`;
